import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserProfile } from '../types/auth.types';
import { authService } from '../services/auth.service';

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, otp?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUser: (data: Partial<UserProfile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const SESSION_DURATION_MS = 60 * 60 * 1000; // 1 hour
const SESSION_CHECK_INTERVAL_MS = 60 * 1000; // check every minute
const LOGIN_TIMESTAMP_KEY = 'login_timestamp';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(authService.isAuthenticated());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let done = false;

    const failsafe = setTimeout(() => {
      if (!done) {
        done = true;
        setIsLoading(false);
      }
    }, 8000);

    const initializeAuth = async () => {
      if (authService.isAuthenticated()) {
        // Check if session has expired before trying to load profile
        const loginTs = Number(localStorage.getItem(LOGIN_TIMESTAMP_KEY) || '0');
        if (loginTs && Date.now() - loginTs > SESSION_DURATION_MS) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
          setUser(null);
          setIsAuthenticated(false);
        } else {
          await refreshUser();
        }
      }
      if (!done) {
        done = true;
        clearTimeout(failsafe);
        setIsLoading(false);
      }
    };

    initializeAuth();
    return () => { done = true; clearTimeout(failsafe); };
  }, []);

  // Periodic session expiry check — auto-logout when 1 hour is up
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSession = () => {
      const loginTs = Number(localStorage.getItem(LOGIN_TIMESTAMP_KEY) || '0');
      if (!loginTs || Date.now() - loginTs > SESSION_DURATION_MS) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
        setUser(null);
        setIsAuthenticated(false);
        window.location.href = '/login';
      }
    };

    const interval = setInterval(checkSession, SESSION_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const login = async (email: string, password: string, otp?: string) => {
    try {
      const response = await authService.login({ email, password, otp });
      localStorage.setItem(LOGIN_TIMESTAMP_KEY, String(Date.now()));
      setUser(response.user);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error: any) {
      const httpStatus = error?.response?.status;
      if (httpStatus === 401) {
        // Token is completely invalid (even refresh failed) — clear state and go to login.
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setUser(null);
        setIsAuthenticated(false);
      } else {
        console.error('Failed to refresh user:', error);
        setIsAuthenticated(authService.isAuthenticated());
      }
    }
  };

  const updateUser = async (data: Partial<UserProfile>) => {
    try {
      const updatedUser = await authService.updateProfile(data);
      setUser(updatedUser);
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUser,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};